// ===================== THE FANGLANDS — hex overworld campaign map =====================
// The world is a hex grid. Every hex is a *node* you fight for: pick one, play a
// timed run in its biome, and clearing it CONQUERS the hex and opens its
// neighbours. Four regions (one per stage biome) sit as ring-shaped territories
// around a central Lair, joined by narrow Crossings.
//
//        ◇ Crystal Cavern ◇                 The map at a glance: the Meadow is
//              ╱        ╲                   the foothold, two middle regions
//   ◆ Meadow ◇            ◇ The Void ◆      branch from it, and both roads
//              ╲        ╱                   converge on the Void.
//         ◇ Ashen Wastes ◇
//
// Rules, in one place:
//   · a hex is AVAILABLE once any neighbouring hex is conquered
//   · a region's LAIR only opens once every other hex in that region is taken
//   · a CROSSING only opens once the region it leads out of has fallen (lair down)
// Node types set the run's length, difficulty, payout and boss cadence, so a
// four-minute Hoard raid and a ten-minute Lair siege feel nothing alike.
import { Save } from '../engine/save.js';
import { RNG } from '../engine/rng.js';
import { hexKey, hexNeighbors, hexRing, hexToPixel } from '../engine/hex.js';

// ---------------------------------------------------------------- node types
// `diff` scales enemy HP/damage on top of the region's own difficulty;
// `bounty` is a one-time first-clear payout; `buff` is the TERRITORY BOON the
// hex lends you for the run (same shape as metaBonuses(), so it folds straight
// into the Player constructor).
//
// `gold` is a CORRECTIVE multiplier, not a reward dial — read it with `payout`.
// Run structure already swings income enormously on its own: a Lair runs 10
// minutes with six boss-and-chest cycles against a Skirmish's five minutes and
// two, and the spawn rate climbs the whole time. Left uncorrected that made
// Lairs earn 3.25x a Skirmish PER MINUTE, so farming anything else was simply
// wrong. `gold` pulls those structural differences back toward parity; a value
// below 1 means "this node type earns plenty from its own shape".
//
// `payout` is the modelled result: expected coins for a full run relative to a
// Skirmish in the same biome. That is the honest number to show a player, and
// it is what the world map's detail card displays.
//
//   type       length   gold    payout    coins/min index
//   Hoard        4:00   x1.10     0.83          1.03
//   Skirmish     5:00   x1.00     1.00          1.00
//   Shrine       5:00   x0.95     0.98          0.98
//   Crossing     6:00   x0.90     1.29          1.07
//   Warcamp      7:00   x0.85     1.63          1.16
//   Lair        10:00   x0.50     2.84          1.41
//
// Harder, longer, more gated content keeps a real premium (the Lair still earns
// the best rate on the map) without any hex being the one correct farm.
export const NODE_TYPES = {
  battle: {
    id: 'battle', name: 'Skirmish', icon: '⚔',
    blurb: 'A straight fight. Hold the ground until the finale calls it.',
    duration: 300, diff: 1.00, gold: 1.00, payout: 1.00, bounty: 20, eliteMult: 1,
    buff: { damage: 0.10 }, buffText: '+10% damage',
  },
  elite: {
    id: 'elite', name: 'Warcamp', icon: '☠',
    blurb: 'Thick with elites. They come in packs here.',
    duration: 420, diff: 1.30, gold: 0.85, payout: 1.63, bounty: 45, eliteMult: 3,
    buff: { armor: 2, maxHp: 25 }, buffText: '+2 armor, +25 max HP',
  },
  treasure: {
    id: 'treasure', name: 'Hoard', icon: '◈',
    blurb: 'In and out in four minutes. Not a bigger purse than anywhere else — just a faster one.',
    duration: 240, diff: 1.15, gold: 1.10, payout: 0.83, bounty: 60, eliteMult: 1.5,
    buff: { gold: 0.30, luck: 1 }, buffText: '+30% coins, +1 luck',
  },
  shrine: {
    id: 'shrine', name: 'Shrine', icon: '✶',
    blurb: 'Take the shrine and its blessing is yours in every run to come.',
    duration: 300, diff: 1.10, gold: 0.95, payout: 0.98, bounty: 30, eliteMult: 1,
    buff: { xp: 0.20 }, buffText: '+20% XP',
  },
  bridge: {
    id: 'bridge', name: 'Crossing', icon: '⇢',
    blurb: 'The road into the next region. Whatever waits there comes to meet you.',
    duration: 360, diff: 1.20, gold: 0.90, payout: 1.29, bounty: 50, eliteMult: 1.5,
    buff: { moveSpeed: 0.10, magnet: 0.25 }, buffText: '+10% move speed, +25% pickup range',
  },
  lair: {
    id: 'lair', name: 'Lair', icon: '♛',
    blurb: 'The heart of the region. Bosses arrive relentlessly — end it and the region falls.',
    duration: 600, diff: 1.55, gold: 0.50, payout: 2.84, bounty: 150, eliteMult: 2,
    bossEvery: 90, firstBossAt: 75,
    buff: { projAdd: 1 }, buffText: 'every weapon fires +1 projectile',
  },
};

// ------------------------------------------------------------------- regions
// `center` is the lair's axial coordinate; `ring` lists the six surrounding
// hexes in clockwise ring order (see hexRing). Each region maps onto an
// existing stage, which supplies the biome, roster, boss pool and music.
export const MAP_REGIONS = [
  {
    id: 'meadow', stageId: 'meadow', name: 'The Meadow', color: '#7dff9c',
    weapon: 'aura', weaponName: 'Warding Circle',
    center: [0, 0],
    lair: { name: "The Maw's Hollow", blurb: 'Where the long grass stops growing.' },
    ring: [
      { type: 'treasure', name: 'Sunken Cairn' },
      { type: 'battle', name: 'Thistlefen' },
      { type: 'elite', name: 'Hornet Downs' },
      { type: 'battle', name: 'Gravewood' },
      {
        type: 'shrine', name: 'Whispering Stones',
        boon: { xp: 0.08 }, boonText: '+8% XP in every run',
      },
      { type: 'battle', name: 'Dawnfield', start: true },
    ],
  },
  {
    id: 'cavern', stageId: 'cavern', name: 'Crystal Cavern', color: '#8cb4ff',
    weapon: 'mine', weaponName: 'Void Mine',
    center: [4, -2],
    lair: { name: "Warden's Deep", blurb: 'The cavern narrows, then opens onto something awake.' },
    ring: [
      { type: 'battle', name: 'Shardgate' },
      { type: 'elite', name: 'Splinterhall' },
      { type: 'battle', name: 'Weeping Geode' },
      { type: 'treasure', name: 'Prismvault' },
      {
        type: 'shrine', name: 'The Still Facet',
        boon: { armor: 2 }, boonText: '+2 armor in every run',
      },
      { type: 'battle', name: 'Glasswalk' },
    ],
  },
  {
    id: 'wastes', stageId: 'wastes', name: 'Ashen Wastes', color: '#ff9a5c',
    weapon: 'strikes', weaponName: 'Judgement',
    center: [2, 2],
    lair: { name: 'Pyreclast Throne', blurb: 'Something sits at the centre of the burn and waits.' },
    ring: [
      {
        type: 'shrine', name: 'The Ashen Font',
        boon: { damage: 0.08 }, boonText: '+8% damage in every run',
      },
      { type: 'battle', name: 'Cinderflats' },
      { type: 'elite', name: 'Houndspire' },
      { type: 'battle', name: 'Slagreach' },
      { type: 'treasure', name: 'Emberhoard' },
      { type: 'battle', name: 'Scorchmarch' },
    ],
  },
  {
    id: 'void', stageId: 'void', name: 'The Void', color: '#a06bff',
    weapon: 'zones', weaponName: 'Hex Pools',
    center: [6, 0],
    lair: { name: 'The Devouring Silence', blurb: 'The last hex. Nothing past it — that is the point.' },
    ring: [
      { type: 'elite', name: 'Nullwatch' },
      { type: 'battle', name: 'Driftmere' },
      { type: 'treasure', name: 'The Pale Vault' },
      { type: 'battle', name: 'Hollowmarch' },
      {
        type: 'shrine', name: 'The Last Eye',
        boon: { luck: 1, gold: 0.10 }, boonText: '+1 luck and +10% coins in every run',
      },
      { type: 'battle', name: 'Threshold' },
    ],
  },
];

// --------------------------------------------------------------- crossings
// Each sits in the two-hex gap between regions, touching the rim of both. A
// crossing runs in the destination's biome — you're already in their land.
export const MAP_BRIDGES = [
  { q: 2, r: -1, from: 'meadow', to: 'cavern', name: 'The Undertrail' },
  { q: 1, r: 1, from: 'meadow', to: 'wastes', name: 'The Scorched Road' },
  { q: 5, r: -1, from: 'cavern', to: 'void', name: 'The Fracture' },
  { q: 4, r: 1, from: 'wastes', to: 'void', name: 'The Cinder Gate' },
];

// ------------------------------------------------------------------ build
function buildNodes() {
  const nodes = [];
  for (const region of MAP_REGIONS) {
    const [cq, cr] = region.center;
    const ring = hexRing(cq, cr, 1);
    region.ring.forEach((spec, i) => {
      const { q, r } = ring[i];
      nodes.push({
        id: hexKey(q, r), q, r,
        typeId: spec.type, type: NODE_TYPES[spec.type],
        name: spec.name, regionId: region.id, stageId: region.stageId,
        start: !!spec.start, boon: spec.boon || null, boonText: spec.boonText || '',
      });
    });
    nodes.push({
      id: hexKey(cq, cr), q: cq, r: cr,
      typeId: 'lair', type: NODE_TYPES.lair,
      name: region.lair.name, blurb: region.lair.blurb,
      regionId: region.id, stageId: region.stageId,
      start: false, boon: null, boonText: '', isLair: true,
    });
  }
  for (const b of MAP_BRIDGES) {
    nodes.push({
      id: hexKey(b.q, b.r), q: b.q, r: b.r,
      typeId: 'bridge', type: NODE_TYPES.bridge,
      name: b.name, regionId: b.to, stageId: regionById(b.to).stageId,
      from: b.from, to: b.to, start: false, boon: null, boonText: '', isBridge: true,
    });
  }
  return nodes;
}

export function regionById(id) { return MAP_REGIONS.find((r) => r.id === id) || MAP_REGIONS[0]; }

export const MAP_NODES = buildNodes();
export const NODE_BY_ID = Object.fromEntries(MAP_NODES.map((n) => [n.id, n]));

export function nodeById(id) { return NODE_BY_ID[id] || null; }
export function nodesInRegion(regionId) { return MAP_NODES.filter((n) => n.regionId === regionId && !n.isBridge); }
export function lairOf(regionId) { return MAP_NODES.find((n) => n.isLair && n.regionId === regionId); }
export const START_NODE = MAP_NODES.find((n) => n.start) || MAP_NODES[0];

// Adjacent map nodes (hexes with no node — the empty gaps — are skipped).
export function neighborsOf(node) {
  return hexNeighbors(node.q, node.r).map(({ q, r }) => NODE_BY_ID[hexKey(q, r)]).filter(Boolean);
}

// Undirected adjacency pairs, for drawing the links between hexes.
export function mapLinks() {
  const seen = new Set(), out = [];
  for (const n of MAP_NODES) {
    for (const m of neighborsOf(n)) {
      const key = n.id < m.id ? n.id + '|' + m.id : m.id + '|' + n.id;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push([n, m]);
    }
  }
  return out;
}

// ------------------------------------------------------- THE RECLAMATION
// Territory doesn't stay yours for free. On a real-time clock the horde takes
// a hex back, and you have to go win it again. The rules are deliberately
// forgiving — this is pressure, not punishment:
//   · Lairs and Crossings are FORTIFIED and can never be lost, so nothing you
//     have unlocked can ever close behind you
//   · a contested hex still counts as conquered for adjacency and gating —
//     you lose its benefits, never your access
//   · at most MAX_CONTESTED are ever contested at once, so coming back after a
//     long break means a few runs of clean-up, not a lost campaign
//   · nothing is contested until you hold SIEGE_MIN_HELD hexes
//   · the border is attacked first — interior land falls only if the frontier
//     is already contested
export const SIEGE_INTERVAL_MS = 4 * 60 * 60 * 1000;  // one incursion per 4h
export const MAX_CONTESTED = 3;
export const SIEGE_MIN_HELD = 5;

// A hex the horde is allowed to take: held, not fortified, not already theirs.
export function siegeable(node) {
  if (node.isLair || node.isBridge) return false;
  return Save.isConquered(node.id) && !Save.isContested(node.id);
}

// Border hexes — held land that still touches something you don't hold.
function onFrontier(node) {
  return neighborsOf(node).some((n) => !Save.isConquered(n.id));
}

// Who falls next. Border first, then the interior; stable order, rotated by the
// siege counter so the same hex isn't hit every single time.
function nextSiegeTarget() {
  const pool = MAP_NODES.filter(siegeable);
  if (!pool.length) return null;
  const border = pool.filter(onFrontier);
  const candidates = (border.length ? border : pool).slice().sort((a, b) => a.id.localeCompare(b.id));
  return candidates[Save.siegeCount() % candidates.length];
}

// Advance the incursion clock to `now`. Safe to call as often as you like —
// it catches up on time spent away, bounded by MAX_CONTESTED.
export function tickReclamation(now = Date.now()) {
  const taken = [];
  let clock = Save.siegeClock();
  if (!clock) { Save.setSiegeClock(now + SIEGE_INTERVAL_MS); return taken; }

  // Too little territory to be worth raiding — hold the clock steady.
  if (Save.conqueredCount() - Save.contestedIds().length < SIEGE_MIN_HELD) {
    if (clock < now) Save.setSiegeClock(now + SIEGE_INTERVAL_MS);
    return taken;
  }

  while (now >= clock && Save.contestedIds().length < MAX_CONTESTED) {
    const target = nextSiegeTarget();
    if (!target) break;
    Save.contest(target.id);
    taken.push(target);
    clock += SIEGE_INTERVAL_MS;
  }
  // Never let the clock lag behind, or the next hex would fall the instant you
  // clear this one.
  Save.setSiegeClock(clock < now ? now + SIEGE_INTERVAL_MS : clock);
  return taken;
}

export function contestedNodes() { return Save.contestedIds().map(nodeById).filter(Boolean); }

// Milliseconds until the next incursion — null when nothing is at risk.
export function msToNextSiege(now = Date.now()) {
  if (Save.contestedIds().length >= MAX_CONTESTED) return null;
  if (Save.conqueredCount() - Save.contestedIds().length < SIEGE_MIN_HELD) return null;
  if (!MAP_NODES.some(siegeable)) return null;
  return Math.max(0, Save.siegeClock() - now);
}

// -------------------------------------------------------------- progression
export function isConquered(nodeOrId) {
  return Save.isConquered(typeof nodeOrId === 'string' ? nodeOrId : nodeOrId.id);
}

export function isContested(nodeOrId) {
  return Save.isContested(typeof nodeOrId === 'string' ? nodeOrId : nodeOrId.id);
}

// Held = conquered and not currently in the horde's hands.
export function isHeld(nodeOrId) { return isConquered(nodeOrId) && !isContested(nodeOrId); }

export function regionCleared(regionId) {
  const lair = lairOf(regionId);
  return !!lair && isConquered(lair);
}

export function regionProgress(regionId) {
  const list = nodesInRegion(regionId);
  return {
    done: list.filter(isHeld).length,
    contested: list.filter(isContested).length,
    total: list.length,
  };
}

export function mapProgress() {
  return {
    done: MAP_NODES.filter(isHeld).length,
    contested: MAP_NODES.filter(isContested).length,
    total: MAP_NODES.length,
  };
}

// Why a reachable hex is still shut — null when nothing blocks it.
export function gateReason(node) {
  if (node.isLair) {
    const rest = nodesInRegion(node.regionId).filter((n) => !n.isLair);
    const left = rest.filter((n) => !isConquered(n)).length;
    if (left > 0) return `Take the rest of ${regionById(node.regionId).name} first — ${left} hex${left === 1 ? '' : 'es'} left`;
  }
  if (node.isBridge && !regionCleared(node.from)) {
    return `${regionById(node.from).name} must fall first`;
  }
  return null;
}

// contested → the horde holds it, go take it back · conquered → yours ·
// available → playable now · gated → touching your territory but a rule holds
// it shut · locked → not reachable yet
export function nodeState(node) {
  if (isContested(node)) return 'contested';
  if (isConquered(node)) return 'conquered';
  const reachable = node.start || neighborsOf(node).some(isConquered);
  if (!reachable) return 'locked';
  return gateReason(node) ? 'gated' : 'available';
}

export function availableNodes() { return MAP_NODES.filter((n) => nodeState(n) === 'available'); }

// Permanent bonuses from shrines you hold. A contested shrine goes dark until
// you win it back — that is what makes losing one sting.
export function worldMapBonuses() {
  const b = {};
  for (const n of MAP_NODES) {
    if (!n.boon || !isHeld(n)) continue;
    for (const [k, v] of Object.entries(n.boon)) b[k] = (b[k] || 0) + v;
  }
  return b;
}

// Every shrine on the map with its claimed state (world-map legend).
export function shrineStatus() {
  return MAP_NODES.filter((n) => n.boon).map((n) => ({
    id: n.id, name: n.name, text: n.boonText, region: regionById(n.regionId).name,
    claimed: isHeld(n), contested: isContested(n),
  }));
}

// Retaking ground is harder than taking it, and pays accordingly. The coin
// premium tracks the difficulty premium (~1.25×) on purpose: at the old ×1.5
// a retake was strictly the best farm per run, so leaving hexes contested
// forever was the dominant strategy.
export const RECLAIM = { diff: 1.25, gold: 1.3, bountyPct: 0.5 };

export function reclaimBounty(node) { return Math.round(node.type.bounty * RECLAIM.bountyPct); }

// ------------------------------------------------------------ THREAT TIERS
// Conquest ends; mastery doesn't. Any hex you hold can be re-run at a higher
// THREAT, which makes the horde tougher and the payout bigger. Clearing a tier
// anywhere unlocks the next one everywhere, so you climb once and apply it
// where you like — and each hex remembers its own best tier as a mastery badge.
//
// XP rises with threat too: enemies have far more HP at high tiers, so without
// it your build would crawl. The intent is "deadlier", not "slower".
export const MAX_THREAT = 10;
// Quadratic, not linear: the top tiers have to stay ahead of an Ascension
// stack that grows without limit, or prestige eventually trivialises the game.
// Threat X is meant to be a thing you chase across many Ascensions.
export const THREAT = {
  hp: (t) => 1 + 0.40 * t + 0.06 * t * t,
  dmg: (t) => 1 + 0.20 * t + 0.02 * t * t,
  xp: (t) => 1 + 0.15 * t,
  gold: (t) => 1 + 0.35 * t + 0.03 * t * t,
  elite: (t) => 1 + 0.25 * t,
};

// Roman numerals read better than "Threat 7" on a badge.
const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export function threatLabel(t) { return ROMAN[t] || String(t); }

export function threatOf(node) { return Save.threatOf(node.id); }
// The highest tier you may select anywhere: one past your best clear.
export function threatCeiling() { return Math.min(MAX_THREAT, Save.maxThreat() + 1); }
export function clampThreat(t) { return Math.max(0, Math.min(threatCeiling(), Math.round(t || 0))); }
// First time you beat a tier on a given hex, it pays a mastery bounty.
export function threatBounty(node, tier) {
  return tier > 0 ? Math.round(node.type.bounty * 0.5 * tier) : 0;
}
export function threatProgress() {
  const mastered = MAP_NODES.filter((n) => Save.threatOf(n.id) > 0).length;
  return { best: Save.maxThreat(), ceiling: threatCeiling(), mastered, total: MAP_NODES.length };
}

// ------------------------------------------------------------ HEX ANOMALIES
// Each UTC day, a few held ring hexes wake up strange: a run mutator plus a
// payout premium, delivered through the same runConfigFor() contract as
// everything else. Every mutation is a GENERIC contract field the engine
// already reads (or now reads) — never an `if (anomalyId === ...)` in game.js.
//
// Doctrine (same as NODE_TYPES): `gold` is the CORRECTIVE knob the player
// never sees, `payout` is the modelled outcome the detail card displays.
// `notOn` lists node type ids an anomaly may never land on — Golden Horde and
// Cursed Ground are barred from Warcamps (typeId 'elite'), where they would
// stack on the node's own elite/difficulty identity.
export const ANOMALIES = {
  alphaden: {
    id: 'alphaden', name: 'Alpha Den',
    blurb: 'Alphas den here. Four times the chance the boss slot goes gold.',
    gold: 1.10, payout: 1.15, notOn: [],
    contract: { alphaMult: 4 },
  },
  goldenhorde: {
    id: 'goldenhorde', name: 'Golden Horde',
    blurb: 'Elites swarm in triples and bleed gold.',
    gold: 0.85, payout: 1.25, notOn: ['elite'],
    // eliteMult is a flat SET (it replaces the node×threat value, never
    // multiplies it); each elite kill also drops a gold gem worth 3, which
    // flows through the normal pickup/goldMult path.
    contract: { eliteMult: 3, eliteGoldGem: 3 },
  },
  cursedground: {
    id: 'cursedground', name: 'Cursed Ground',
    blurb: 'The run begins at Curse 3. Thicker, tougher, richer.',
    gold: 0.90, payout: 1.20, notOn: ['elite'],
    contract: { curse: 3 },
  },
  bossrush: {
    id: 'bossrush', name: 'Boss Rush',
    blurb: "Bosses arrive on a Lair's clock.",
    gold: 1.10, payout: 1.20, notOn: [],
    contract: { bossEvery: 90, firstBossAt: 75 },
  },
  swarmtide: {
    id: 'swarmtide', name: 'Swarmtide',
    blurb: 'The swarms come three deep.',
    gold: 1.12, payout: 1.15, notOn: [],
    contract: { swarmMult: 3 },
  },
};

// Dormant until the player holds ANOMALY_MIN_HELD hexes (the Reclamation's
// threshold, on purpose); 2 picks a day until ANOMALY_FULL_HELD are held,
// then 3. All date math takes `now` as a parameter (tickReclamation-style)
// so tests can drive the calendar.
export const ANOMALY_MIN_HELD = SIEGE_MIN_HELD;
export const ANOMALY_FULL_HELD = 12;

export function utcDay(now = Date.now()) { return Math.floor(now / 86400000); }
// Milliseconds until the anomaly reset (UTC midnight).
export function msToAnomalyReset(now = Date.now()) { return (utcDay(now) + 1) * 86400000 - now; }

// A hex an anomaly may land on: a conquered ring hex. Lairs and Crossings are
// fortified; shrines are eligible. Contested still counts as conquered here —
// a contested hex keeps its anomaly, and the retake run gets the mutator plus
// both premiums.
function anomalyEligible(node) {
  if (node.isLair || node.isBridge) return false;
  return Save.isConquered(node.id);
}

// Roll the day's picks: seeded shuffle of the eligible hexes, take N, then
// assign mutators by seeded draw without replacement, skipping notOn
// mismatches. Deterministic for a given (anomalySeed, day, territory).
function rollAnomalies(day) {
  if (Save.conqueredCount() < ANOMALY_MIN_HELD) return [];
  const eligible = MAP_NODES.filter(anomalyEligible);
  if (!eligible.length) return [];
  const count = Math.min(Save.conqueredCount() >= ANOMALY_FULL_HELD ? 3 : 2, eligible.length);
  const rng = new RNG((Save.anomalySeed() ^ Math.imul(day, 0x9e3779b9)) >>> 0);
  const hexes = rng.shuffle(eligible.slice()).slice(0, count);
  const pool = Object.keys(ANOMALIES);
  const picks = [];
  for (const node of hexes) {
    const avail = pool.filter((id) => !(ANOMALIES[id].notOn || []).includes(node.typeId));
    if (!avail.length) continue;
    const mutId = rng.pick(avail);
    pool.splice(pool.indexOf(mutId), 1);
    picks.push({ nodeId: node.id, mutId });
  }
  return picks;
}

// Today's anomalies. The first call of a new UTC day rolls and PERSISTS the
// picks; the stored picks are then canonical for the day — mid-day conquests
// and reclaims never reshuffle them.
export function anomaliesForToday(now = Date.now()) {
  const day = utcDay(now);
  const stored = Save.anomalies();
  if (stored.day === day) return stored.picks;
  const picks = rollAnomalies(day);
  Save.setAnomalies(day, picks);
  return picks;
}

// The anomaly on a hex today, or null.
export function anomalyFor(nodeOrId, now = Date.now()) {
  const id = typeof nodeOrId === 'string' ? nodeOrId : nodeOrId.id;
  const pick = anomaliesForToday(now).find((p) => p.nodeId === id);
  return pick ? ANOMALIES[pick.mutId] : null;
}

// The run parameters a node imposes on top of its region's stage. A contested
// hex is dug in: tougher, but it pays more and lends the same territory boon.
// `tier` is the chosen threat level (0 = the hex as first conquered).
export function runConfigFor(node, tier = 0, now = Date.now()) {
  const t = node.type;
  const region = regionById(node.regionId);
  const retake = isContested(node);
  const T = clampThreat(tier);
  const cfg = {
    nodeId: node.id,
    nodeName: node.name,
    nodeType: t.name,
    reclaim: retake,
    threat: T,
    duration: t.duration,
    // `diff` is the node's own difficulty (it also thickens the spawn rate);
    // threat rides on top as separate HP / damage / XP multipliers.
    diff: t.diff * (retake ? RECLAIM.diff : 1),
    threatHp: THREAT.hp(T),
    threatDmg: THREAT.dmg(T),
    threatXp: THREAT.xp(T),
    gold: t.gold * (retake ? RECLAIM.gold : 1) * THREAT.gold(T),
    // Expected coins for a full run vs a Skirmish in this biome — the number
    // worth showing a player, since raw `gold` is a corrective knob.
    payout: t.payout * (retake ? RECLAIM.gold : 1) * THREAT.gold(T),
    eliteMult: t.eliteMult * THREAT.elite(T),
    bossEvery: t.bossEvery,
    firstBossAt: t.firstBossAt,
    // Territory boon: the region lends a weapon, the node type lends a buff.
    weapon: region.weapon,
    weaponName: region.weaponName,
    buff: t.buff || {},
    buffText: t.buffText || '',
  };
  // Today's anomaly rides on top: its mutation fields flat-SET onto the
  // contract (never multiplied in), and both premiums compose with the
  // node/retake/threat ones — payout stays the honest, final figure.
  const anomaly = anomalyFor(node, now);
  if (anomaly) {
    Object.assign(cfg, anomaly.contract);
    cfg.gold *= anomaly.gold;
    cfg.payout *= anomaly.payout;
    cfg.anomaly = anomaly.id;
    cfg.anomalyName = anomaly.name;
  }
  return cfg;
}

// ------------------------------------------------------- legacy save backfill
// Before the map, progress was a flat list of unlocked stage ids in this order.
// Reaching stage N meant clearing stage N-1, so we hand those regions over
// wholesale rather than making returning players re-take ground they earned.
const LEGACY_ORDER = ['meadow', 'cavern', 'wastes', 'void'];

export function legacySeedIds(unlockedStages) {
  const reached = LEGACY_ORDER.reduce((hi, id, i) => (unlockedStages.includes(id) ? i : hi), 0);
  const clearedRegions = new Set(LEGACY_ORDER.slice(0, reached));
  const ids = [];
  for (const id of clearedRegions) for (const n of nodesInRegion(id)) ids.push(n.id);
  for (const n of MAP_NODES) {
    // A crossing is only handed over if both ends were already in play.
    if (n.isBridge && clearedRegions.has(n.from) && LEGACY_ORDER.indexOf(n.to) <= reached) ids.push(n.id);
  }
  return ids;
}

// Runs once per save, on first load after the map ships.
export function seedMapFromLegacySave() {
  return Save.seedMap(legacySeedIds(Save.data.stages || ['meadow']));
}
seedMapFromLegacySave();

// ------------------------------------------------------ THE WEEKLY GAUNTLET
// One attempt per ISO week, for every player, against the SAME seeded horde:
// a fixed 6-minute contract at flat Threat-III-grade scaling (global parity —
// personal scaling is deliberately not v1). It lives on the world-map screen
// as its own banner, not as a hex, and unlocks when the first Lair falls.

// ISO-8601 week from UTC (the Thursday algorithm: a week belongs to the year
// its Thursday falls in). `now`-parameterised like every date function here.
export function isoWeek(now = Date.now()) {
  const d = new Date(now);
  const day = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const dow = new Date(day).getUTCDay() || 7;          // Mon=1 … Sun=7
  const thursday = day + (4 - dow) * 86400000;
  const year = new Date(thursday).getUTCFullYear();
  const week = Math.floor((thursday - Date.UTC(year, 0, 1)) / 86400000 / 7) + 1;
  return { year, week };
}

// A single comparable number for "which week is this" (year-safe).
export function gauntletWeekKey(now = Date.now()) {
  const { year, week } = isoWeek(now);
  return year * 100 + week;
}

// The week's shared seed — a global constant, the same for every player.
export function gauntletSeed(now = Date.now()) {
  const { year, week } = isoWeek(now);
  const str = `NEONFANG-GAUNTLET:${year}:${week}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

// The fixed contract (v1 ships at flat T3-difficulty parity: threatHp/Dmg/Xp
// are THREAT.hp/dmg/xp(3), frozen here on purpose so a later tiers rebalance
// can't silently move the Gauntlet). gold stays 1.0 — no threat-gold
// multiplier; the reward is the flat clear bounty.
export const GAUNTLET = {
  duration: 360, diff: 1.35, eliteMult: 1.5, firstBossAt: 90, bossEvery: 120,
  threatHp: 2.74, threatDmg: 1.78, threatXp: 1.45, gold: 1.0,
  bounty: 1000,
};

// Composite score for a Gauntlet run — deterministic from the run summary, so
// two players on the same weekly seed can compare numbers with no backend.
// Surviving pays a flat block (5000) strictly larger than the maximum
// death-time credit (duration × 10 = 3600), so dying late can never outscore
// winning; kills, combo and level reward playing the shared horde hard rather
// than hiding from it.
export function gauntletScore(s) {
  const time = s.won ? 5000 : Math.floor(Math.min(s.seconds, GAUNTLET.duration)) * 10;
  return time + (s.kills || 0) * 10 + (s.bestCombo || 0) * 15 + (s.level || 0) * 100;
}

// The Gauntlet opens once any region has fallen.
export function gauntletUnlocked() {
  return MAP_REGIONS.some((r) => regionCleared(r.id));
}

// The week's run contract. The stage rotates through the campaign order; the
// region lends its weapon as usual, but no node buff — character choice is
// free and the horde is the leveller.
export function gauntletConfigFor(now = Date.now()) {
  const { week } = isoWeek(now);
  const region = regionById(LEGACY_ORDER[week % 4]);
  return {
    gauntlet: true,
    gauntletWeek: gauntletWeekKey(now),
    seed: gauntletSeed(now),
    nodeId: null,
    nodeName: 'THE WEEKLY GAUNTLET',
    nodeType: 'Gauntlet',
    stageId: region.stageId,
    duration: GAUNTLET.duration,
    diff: GAUNTLET.diff,
    threatHp: GAUNTLET.threatHp,
    threatDmg: GAUNTLET.threatDmg,
    threatXp: GAUNTLET.threatXp,
    gold: GAUNTLET.gold,
    eliteMult: GAUNTLET.eliteMult,
    bossEvery: GAUNTLET.bossEvery,
    firstBossAt: GAUNTLET.firstBossAt,
    // Horde-cap parity: every device fights the same-size horde on the shared
    // seed, whatever its own tier would allow.
    maxEnemies: 160,
    weapon: region.weapon,
    weaponName: region.weaponName,
    buff: {},
    buffText: '',
  };
}

// A run seed as the player sees it: 8 uppercase hex chars split 4-4
// ("A7F3-09C2"). Shown on the results screen and the pause stat sheet.
export function formatSeed(seed) {
  const h = (seed >>> 0).toString(16).toUpperCase().padStart(8, '0');
  return h.slice(0, 4) + '-' + h.slice(4);
}

// Pixel layout for the whole map, normalised into a tight SVG viewBox.
export function mapLayout(size = 10, pad = 2) {
  const pts = MAP_NODES.map((n) => ({ node: n, ...hexToPixel(n.q, n.r, size) }));
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  const minX = Math.min(...xs) - size - pad, maxX = Math.max(...xs) + size + pad;
  const minY = Math.min(...ys) - size - pad, maxY = Math.max(...ys) + size + pad;
  const pos = {};
  for (const p of pts) pos[p.node.id] = { x: p.x, y: p.y };
  return { pos, size, viewBox: `${minX} ${minY} ${maxX - minX} ${maxY - minY}` };
}
